/**
 * The backend override — #449. Developer settings, every build EXCEPT production.
 *
 * ## Production contains none of this, and that is enforced on the artifact
 *
 * Nothing in production code may import this module unconditionally. Every
 * importer sits behind `import.meta.env.MODE !== 'production'`, which Vite
 * folds to `false` in `vite build --mode production`, so the importing branch
 * is dead and this module — the host list, the storage key, the copy — is
 * never emitted. `backendOverride.build.test.ts` builds production and greps
 * the JavaScript for every string below; its companion builds development and
 * requires them, so the guard cannot pass by reading nothing.
 *
 * The gate is the MODE, not `DEV`: `build:dev` and `build:stag` are
 * production-like builds (`DEV` is false in both) that must still carry this.
 *
 * **Keep the data in here.** Rollup drops dead components readily and keeps
 * referenced data stubbornly — the probe fixture leaked that way (#329). A host
 * list or a sentence moved into a module that production imports for another
 * reason ships, whatever the component that renders it does.
 *
 * **This module has no top-level side effects**, which is what lets the
 * bundler drop it once its importers are dead. Only `const` data and
 * functions; nothing reads `window` or storage at evaluation.
 */

/** Stores an ID, never a URL. See `readStoredBackend`. */
export const BACKEND_STORAGE_KEY = 'tracke5.developer.backend'

export type BackendId = 'local' | 'dev' | 'staging'

export type Backend = {
  id: BackendId
  label: string
  base: string
  /** Page origins this backend's CORS accepts, exactly — scheme, host and port. */
  origins: readonly string[]
  /** Shown beside the choice and again in its confirm step. */
  warning: string | null
}

/**
 * The page origins a local web server runs on.
 *
 * 5173 is `vite`'s port — every `make web-*` target and `make e2e-up` pin it
 * with `--strictPort`. 4173 is `vite preview`, which is how a `build:dev`
 * bundle is looked at locally. Both hostnames, because the loopback address is
 * the same machine and every allow-list below names both.
 *
 * Not 5174. `dev-api` rejects it (#449, measured 2026-09-21).
 */
const LOCAL_ORIGINS = [
  'http://localhost:5173',
  'http://127.0.0.1:5173',
  'http://localhost:4173',
  'http://127.0.0.1:4173',
] as const

/** The deployed dev web app. `app-stag` exists as a hostname and no API accepts it yet (#53). */
const APP_DEV_ORIGIN = 'https://app-dev.tracke5.com'

/**
 * The three backends, and the origins each one's CORS accepts.
 *
 * Sources, so the next person can re-check rather than re-derive:
 * - Local: `api/src/config.py`'s `cors_origins` default in the backend repo —
 *   local dev-server ports only. A page on `app-dev` cannot use it anyway:
 *   `localhost` there is the VIEWER's machine, not the developer's.
 * - Dev and Staging: the dev `cors_origins` output in the backend's
 *   `.github/workflows/deploy-api.yml`, shared by `expense-monitor-api` and
 *   `expense-monitor-api-stag`; confirmed by preflight in #449's table.
 *
 * Staging is `expense-monitor-api-stag` in the DEV GCP project: Plaid
 * PRODUCTION credentials over the dev database. The warning says both halves.
 */
const BACKENDS: readonly Backend[] = [
  {
    id: 'local',
    label: 'Local stack',
    base: 'http://localhost:8081',
    origins: LOCAL_ORIGINS,
    warning: null,
  },
  {
    id: 'dev',
    label: 'Dev',
    base: 'https://dev-api.tracke5.com',
    origins: [...LOCAL_ORIGINS, APP_DEV_ORIGIN],
    warning: null,
  },
  {
    id: 'staging',
    label: 'Staging',
    base: 'https://stag-api.tracke5.com',
    origins: [...LOCAL_ORIGINS, APP_DEV_ORIGIN],
    warning: 'Plaid production: real bank data. Shared database with dev.',
  },
]

/** Only the backends whose CORS accepts `origin`. Offering the rest would offer a broken app. */
export function backendsFor(origin: string): Backend[] {
  return BACKENDS.filter((b) => b.origins.includes(origin))
}

/** The browser's storage, reached lazily: merely touching `localStorage` throws in some contexts. */
const browserStorage = (): Storage => window.localStorage

function isBackendId(value: string | null): value is BackendId {
  return BACKENDS.some((b) => b.id === value)
}

/**
 * The stored choice, or `null` for none, unreadable or unrecognised.
 *
 * Validated against the ids above rather than trusted. A URL stored here would
 * let anything able to write this origin's storage point every authenticated
 * request — bearer token attached — at a host of its choosing. An id can only
 * ever select one of three hosts written in this file.
 */
export function readStoredBackend(storage: () => Storage = browserStorage): BackendId | null {
  try {
    const value = storage().getItem(BACKEND_STORAGE_KEY)
    return isBackendId(value) ? value : null
  } catch {
    return null
  }
}

/** `false` when storage refused — the caller must not reload onto a choice that was not saved. */
export function storeBackend(id: BackendId, storage: () => Storage = browserStorage): boolean {
  try {
    storage().setItem(BACKEND_STORAGE_KEY, id)
    return true
  } catch {
    return false
  }
}

export function clearStoredBackend(storage: () => Storage = browserStorage): boolean {
  try {
    storage().removeItem(BACKEND_STORAGE_KEY)
    return true
  } catch {
    return false
  }
}

export type Resolution = {
  base: string
  /** True only when the base in use differs from the build's own default. */
  overridden: boolean
}

/**
 * Which API base this page load uses.
 *
 * A stored choice applies only if the current origin is on that backend's CORS
 * list; otherwise the build default stands. A choice made on localhost and
 * carried to `app-dev` by nothing more than the same browser would otherwise
 * aim the deployed site at `localhost:8081` and fail every request.
 */
export function resolveBackend({
  buildDefault,
  origin,
  stored,
}: {
  buildDefault: string
  origin: string
  stored: BackendId | null
}): Resolution {
  const chosen = backendsFor(origin).find((b) => b.id === stored)
  const base = chosen?.base ?? buildDefault
  return { base, overridden: base !== buildDefault }
}

/**
 * The one call `api/client` makes, at module evaluation, from the real window.
 *
 * **It announces an override in the console**, once per page load. Useful in
 * its own right — a developer reading the network tab learns why requests go
 * somewhere the build did not say — and it is the only text in this module
 * that nothing but `api/client`'s call reaches. `backendOverride.build.test.ts`
 * requires it in the development bundle, which is how gating that one call on
 * `DEV` instead of MODE — `build:dev` would then silently ignore every stored
 * choice while the page offering them still shipped — goes red.
 */
export function resolveApiBase(buildDefault: string): string {
  const { base, overridden } = resolveBackend({
    buildDefault,
    origin: window.location.origin,
    stored: readStoredBackend(),
  })
  if (overridden) {
    console.info(
      `TrackE5 API override: ${base} instead of this build's ${buildDefault}. ` +
        'Change it in Settings > Developer.',
    )
  }
  return base
}
